// Экспорт результатов одной игры лиги в PNG: карточка с местами, именами и очками.
// Ручной рендер на canvas — общие помощники в canvasShared.js.

import {
  COLORS, FONT_FAMILY, ensureFontsReady, canvasToBlob, triggerDownload, ellipsize,
} from '@/views/fnf-league/canvasShared'

const WIDTH = 1080
const PADDING = 48
const HEADER_HEIGHT = 190
const ROW_HEIGHT = 96
const BADGE_RADIUS = 26

// Золото / серебро / бронза для бейджей мест, 4-е и дальше — приглушённый
const PLACE_BADGE_COLORS = {
  1: '#ffd166',
  2: '#c3cbdc',
  3: '#d8945b',
}

function gameTitle(game) {
  // Номер игры отличает игры одного тура в одной подгруппе
  const gameNumber = game.game_number ? ` — Игра ${game.game_number}` : ''
  switch (game.stage) {
    case 'group': return `Группа ${game.group_letter} — Тур ${game.tour}${gameNumber}`
    case 'semifinal': return `Полуфинал${game.tour ? ` ${game.tour}` : ''}${gameNumber}`
    case 'final': return `Финал${gameNumber}`
    default: return game.name
  }
}

function formatDate(scheduledAt) {
  if (!scheduledAt) return null
  const date = new Date(scheduledAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default async function exportLeagueGameImage({ game }) {
  await ensureFontsReady()

  const results = [...game.results].sort((a, b) => a.place - b.place)
  const height = HEADER_HEIGHT + PADDING + results.length * ROW_HEIGHT + PADDING

  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = height
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, WIDTH, height)

  // Шапка: название лиги, игра, дата и ведущий
  ctx.fillStyle = COLORS.cardTop
  ctx.fillRect(0, 0, WIDTH, HEADER_HEIGHT)
  ctx.textBaseline = 'middle'
  ctx.fillStyle = COLORS.headerText
  ctx.font = `bold 52px ${FONT_FAMILY}`
  ctx.fillText('ФНФ ЛИГА', PADDING, 58)
  ctx.font = `bold 34px ${FONT_FAMILY}`
  ctx.fillText(`Сезон ${game.season} · ${gameTitle(game)}`, PADDING, 116)
  const dateText = formatDate(game.scheduled_at)
  const infoParts = [dateText, game.host_name ? `ведущий ${game.host_name}` : null].filter(Boolean)
  if (infoParts.length) {
    ctx.font = `24px ${FONT_FAMILY}`
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
    ctx.fillText(infoParts.join(' · '), PADDING, 158)
  }

  // Строки результатов: бейдж места, имя, очки и лиговые баллы справа.
  // Баллы приходят с бэкенда только у групповых игр — в плей-офф колонки нет.
  const ballsText = result => (result.balls == null ? '' : `+${Number(result.balls).toLocaleString('ru-RU')}`)
  const hasBalls = results.some(result => result.balls != null)
  ctx.font = `bold 34px ${FONT_FAMILY}`
  const ballsColumnWidth = hasBalls
    ? Math.max(...results.map(result => ctx.measureText(ballsText(result)).width))
    : 0
  const ballsRight = WIDTH - PADDING
  const pointsRight = hasBalls ? ballsRight - ballsColumnWidth - 36 : ballsRight
  const nameLeft = PADDING + BADGE_RADIUS * 2 + 28
  let y = HEADER_HEIGHT + PADDING

  results.forEach((result, index) => {
    const middle = y + ROW_HEIGHT / 2
    // Не сыгравший (неявка): очков нет, по регламенту последнее место —
    // строка приглушена, вместо очков прочерк
    const didNotPlay = result.did_not_play || result.points == null

    ctx.fillStyle = result.place === 1 ? COLORS.rowHighlight : (index % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd)
    ctx.fillRect(PADDING / 2, y, WIDTH - PADDING, ROW_HEIGHT)

    // Бейдж места (у не сыгравшего — без медальных цветов)
    const badgeColor = didNotPlay ? COLORS.muted : (PLACE_BADGE_COLORS[result.place] || COLORS.muted)
    ctx.beginPath()
    ctx.arc(PADDING + BADGE_RADIUS, middle, BADGE_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = badgeColor
    ctx.fill()
    ctx.fillStyle = COLORS.background
    ctx.font = `bold 28px ${FONT_FAMILY}`
    const placeText = String(result.place)
    const placeWidth = ctx.measureText(placeText).width
    ctx.fillText(placeText, PADDING + BADGE_RADIUS - placeWidth / 2, middle + 1)

    // Лиговые баллы за игру — крайняя правая колонка
    if (hasBalls) {
      ctx.font = `bold 34px ${FONT_FAMILY}`
      ctx.fillStyle = result.place === 1 ? COLORS.accent : COLORS.muted
      const text = ballsText(result)
      ctx.fillText(text, ballsRight - ctx.measureText(text).width, middle)
    }

    // Очки — справа, у победителя акцентом; у не сыгравшего — прочерк
    ctx.font = `bold 36px ${FONT_FAMILY}`
    ctx.fillStyle = result.place === 1 ? COLORS.accent : (didNotPlay ? COLORS.muted : COLORS.text)
    const pointsText = didNotPlay ? '—' : Number(result.points).toLocaleString('ru-RU')
    const pointsWidth = ctx.measureText(pointsText).width
    ctx.fillText(pointsText, pointsRight - pointsWidth, middle)

    // Имя — между бейджем и очками
    ctx.font = `bold 32px ${FONT_FAMILY}`
    ctx.fillStyle = result.place === 1 ? COLORS.headerText : (didNotPlay ? COLORS.muted : COLORS.text)
    const nameMaxWidth = pointsRight - pointsWidth - nameLeft - 24
    ctx.fillText(ellipsize(ctx, result.name, nameMaxWidth), nameLeft, middle)

    y += ROW_HEIGHT
  })

  const blob = await canvasToBlob(canvas)
  const titleSlug = gameTitle(game).replace(/[^\wа-яёА-ЯЁ]+/gu, '_').replace(/^_+|_+$/g, '')
  triggerDownload(blob, `ФНФ_Лига_Сезон${game.season}_${titleSlug}.png`)
}
